import React from 'react';
import { ReportBaseProps } from '../WeeklyReport';
import {
  AssetChange as AssetChangeInternal,
  AssetChangeProps as AssetChangeProps_,
} from '../../components/AssetChange';
import {
  addSignCommaFormat,
  addSignCommaFormatPercentage,
  commaFormat,
  getSign,
} from '../../utils/formatter';
import { FREQUENCY, Frequency } from 'pages/NewReport';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';

export type AssetChangeProps = AssetChangeProps_;

type DateInformationArgs = {
  year: number;
  range: {
    from: {
      month: number;
      day: number;
    };
    to: {
      month: number;
      day: number;
    };
  };
};

const getDateInformation = ({
  year,
  range,
}: DateInformationArgs): React.FC<Record<string, unknown>> => () => {
  const { from, to } = range;
  return (
    <div className="flex flex-col items-center flex-1">
      <div className="flex items-baseline mb-1 text-sm leading-tight">
        {year}年
      </div>
      <div className="text-base font-bold">
        {from.month}月{from.day}日 〜 {to.month}月{to.day}日
      </div>
    </div>
  );
};

export const AssetChange = ({
  Base,
  CreatedAt,
  isItemMask,
  NextQuery,
  PreviousQuery,
  setQuery,
}: {
  Base: ReportBaseProps;
  CreatedAt: string | null;
  isItemMask: boolean;
  NextQuery: string | null;
  PreviousQuery: string | null;
  setQuery: (q: string, frequency: Frequency) => void;
}) => {
  const today = new Date();
  const from = yyyymmddToDate(Base.From.toString()) || today;
  const to =
    yyyymmddToDate(Base.To.toString()) ||
    new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);

  const year = from.getFullYear();

  const totalAssetAmount =
    Base.TotalAssetAmount !== null ? commaFormat(Base.TotalAssetAmount) : null;
  const assetAmountChange =
    Base.DiffTotalAssetAmount !== null
      ? addSignCommaFormat(Base.DiffTotalAssetAmount)
      : null;
  const assetAmountChangeSign =
    Base.DiffTotalAssetAmount !== null
      ? getSign(Base.DiffTotalAssetAmount)
      : null;
  const comparisonToLastWeek =
    Base.DiffTotalAssetRate !== null
      ? addSignCommaFormatPercentage(Base.DiffTotalAssetRate, {
          numberOfDecimals: 2,
        })
      : null;
  const asset: AssetChangeProps['asset'] =
    totalAssetAmount !== null &&
    assetAmountChange !== null &&
    assetAmountChangeSign !== null &&
    comparisonToLastWeek !== null
      ? {
          totalAssetAmount,
          assetAmountChange,
          assetAmountChangeSign,
          comparisonToLast: comparisonToLastWeek,
        }
      : {
          totalAssetAmount: '---',
          assetAmountChange: '---',
          comparisonToLast: '---',
        };
  const createdAt = CreatedAt ? new Date(CreatedAt.replace(' ', 'T')) : null;
  const DateInformation = getDateInformation({
    year,
    range: {
      from: { month: from.getMonth() + 1, day: from.getDate() },
      to: { month: to.getMonth() + 1, day: to.getDate() },
    },
  });

  const goNext =
    NextQuery !== null
      ? (): void => {
          setQuery(NextQuery, FREQUENCY.WEEKLY);
        }
      : null;
  const goPrevious =
    PreviousQuery !== null
      ? (): void => {
          setQuery(PreviousQuery, FREQUENCY.WEEKLY);
        }
      : null;

  return (
    <AssetChangeInternal
      {...{
        DateInformation,
        updateInfoString: createdAt
          ? `${createdAt.getFullYear()}/${createdAt.getMonth() +
              1}/${createdAt.getDate()} ${createdAt
              .getHours()
              .toString()
              .padStart(2, '0')}:${createdAt
              .getMinutes()
              .toString()
              .padStart(2, '0')}`
          : null,
        asset,
        periodVerbose: '週',
        isItemMask,
        goNext,
        goPrevious,
      }}
    />
  );
};
